import React, { FC, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { SelectTypes } from '@nhl/nhl-breakaway-ui';

import { StatsState } from '~/core/types';
import { dataTableActionCreators } from '~/redux/ducks/playersAndTeams/dataTable';
import { commonMessages } from '~/utility/commonMessages';
import Label from '~/views/components/label';

import { RowSizeContainer, StyledSelect } from './styles';

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];
const MIN_ROWS_TO_ENABLE_SELECT = 25;

const computePageSizes = (total: number): Array<number> => {
    const pageOptions = PAGE_SIZE_OPTIONS.filter((value) => total >= value);
    if (PAGE_SIZE_OPTIONS.length > pageOptions.length) {
        pageOptions.push(total);
    }
    return pageOptions;
};

const getPageSizeOptions = (total: number): Array<SelectTypes.BasicOption> =>
    computePageSizes(total).map((size) => ({
        key: size,
        label: `${size}`,
        value: size,
    }));

const RowSizeSelect: FC = () => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const dataTable = useSelector((state: StatsState) => state.playersAndTeams.dataTable);
    const { pageSize } = dataTable;
    const { total } = dataTable.dataset;
    const disableSelect = total <= MIN_ROWS_TO_ENABLE_SELECT;

    const pageSizeOptions = useMemo(() => getPageSizeOptions(total), [total]);
    const selectedPageSize = useMemo(
        () => pageSizeOptions.find((option) => option.value === Math.min(pageSize, total)),
        [pageSizeOptions, pageSize, total],
    );

    const handleOnOptionChange = (_, pageSizeOption: SelectTypes.BasicOption) => {
        dispatch(dataTableActionCreators.setPageSize(pageSizeOption.value));
    };

    return (
        <RowSizeContainer>
            <Label htmlFor="rows">{intl.formatMessage(commonMessages.labels.rows)}</Label>
            <StyledSelect
                id="rows"
                isSelectDisabled={disableSelect}
                onOptionChange={handleOnOptionChange}
                options={pageSizeOptions}
                selectedOption={selectedPageSize}
                optionsBoxAriaLabel={intl.formatMessage(commonMessages.ariaLabels.rowSize.optionsBox)}
                dropdownButtonAriaLabel={intl.formatMessage(commonMessages.ariaLabels.rowSize.dropdownButton)}
            />
        </RowSizeContainer>
    );
};

export default RowSizeSelect;
